<template>
  <!-- Main container for the order detail page -->
  <div class="bg-light min-vh-100">
    <main class="container">
      <div class="row justify-content-center">
        <div class="col-lg-10">
          <div class="card shadow-sm">
            <div class="card-body">
              <!-- Header: title + status dropdown -->
              <div class="d-flex justify-content-between align-items-center mb-2">
                <!-- Order ID title -->
                <h1 class="h4 mb-0">Device #{{ order?.order_id }}</h1>

                <!-- Actions on the right -->
                <div class="d-flex align-items-center gap-2">
                  <!-- Print full order sheet -->
                  <button
                    v-if="canPrintOrder"
                    class="btn btn-secondary btn-sm"
                    @click="openPrintView"
                  >
                    Print Sheet
                  </button>

                  <!-- Print 2" x 1" serial barcode label -->
                  <button
                    v-if="order && order.serial_number"
                    class="btn btn-outline-secondary btn-sm"
                    @click="printSerialLabel"
                  >
                    Print Serial Label
                  </button>

                  <!-- Status dropdown, shown if order is loaded and not locked (or supervisor) -->
                  <div v-if="order && (!isLocked || isSupervisor)">
                    <select
                      v-model="localOrderStatus"
                      class="form-select form-select-sm"
                      style="min-width: 160px;"
                      @change="onStatusChange"
                      :disabled="updatingStatus"
                    >
                      <option :value="order.order_status">
                        {{ order.order_status }}
                      </option>
                      <option
                        v-for="t in allowedTransitions"
                        :key="t"
                        :value="t"
                      >
                        {{ t }}
                      </option>
                    </select>
                  </div>
                  <!-- Show badge for locked statuses (non-supervisors) -->
                  <span v-else-if="order" class="badge" :class="statusBadgeClass">
                    {{ order.order_status }}
                    <template v-if="order.order_status === 'HOLD' && order.previous_status">
                      (was: {{ order.previous_status }})
                    </template>
                  </span>
                </div>
              </div>

              <!-- Help text -->
              <p class="help mb-3">
                Update step results as you work. Completing the order locks it for further changes.
              </p>

              <!-- Loading spinner -->
              <div v-if="loading" class="text-center py-5">
                <div class="spinner-border" role="status"></div>
              </div>

              <!-- Error message -->
              <div v-else-if="error" class="alert alert-danger py-2">
                {{ error }}
              </div>

              <!-- Main order content -->
              <div v-else-if="order">
                <!-- Device summary section -->
                <div class="row mb-3">
                  <div class="col-md-4">
                    <div class="mb-1 fw-semibold d-flex align-items-center gap-1">
                      Device
                      <button
                        v-if="isSupervisor && !isLocked && !editingDeviceModel"
                        class="btn btn-link btn-sm p-0 text-muted"
                        title="Change device model"
                        @click="editingDeviceModel = true"
                      >
                        <small>&#9998;</small>
                      </button>
                    </div>
                    <div v-if="editingDeviceModel && isSupervisor" class="mb-1">
                      <select
                        v-model="selectedDeviceModelId"
                        class="form-select form-select-sm"
                        :disabled="savingDeviceModel"
                        @change="onDeviceModelChange"
                      >
                        <option :value="null">-- No model --</option>
                        <option
                          v-for="dm in deviceModels"
                          :key="dm.device_model_id"
                          :value="dm.device_model_id"
                        >
                          {{ dm.make }} {{ dm.model_name }}
                        </option>
                      </select>
                      <span v-if="savingDeviceModel" class="spinner-border spinner-border-sm ms-1"></span>
                    </div>
                    <div v-else>
                      <div class="text-muted">
                        {{ order.device_make }} {{ order.device_model }}
                      </div>
                      <div class="text-muted small">
                        {{ order.device_commonname }}
                      </div>
                    </div>
                  </div>

                  <div class="col-md-4">
                    <div class="mb-1 fw-semibold">Serial</div>
                    <div class="text-muted">{{ order.serial_number }}</div>
                  </div>

                  <div class="col-md-4">
                    <div class="mb-1 fw-semibold">Template</div>

                    <!-- Supervisor/Admin: can change template -->
                    <div v-if="isSupervisor">
                      <select
                        v-if="templates && templates.length"
                        v-model.number="selectedTemplateId"
                        class="form-select form-select-sm"
                        :disabled="updatingTemplate || isLocked"
                        @change="onTemplateChange"
                      >
                        <option
                          v-for="tpl in templates"
                          :key="tpl.template_id"
                          :value="tpl.template_id"
                        >
                          {{ tpl.template_name }}
                        </option>
                      </select>

                      <div v-else class="text-muted small">
                        Loading templates...
                      </div>

                      

                      <div v-if="updatingTemplate" class="text-muted small">
                        Updating template...
                      </div>
                    </div>

                    <!-- Technicians/others: read-only display -->
                    <div v-else class="text-muted">
                      {{ currentTemplateDisplay }}
                    </div>
                  </div>
                </div>
                <!-- Intake condition (read-only) -->
<div class="row mb-3" v-if="order.intake_condition">
  <div class="col-12">
    <div class="mb-1 fw-semibold">Intake Condition</div>
    <div class="text-muted">
      {{ order.intake_condition }}
    </div>
  </div>
</div>

                <!-- Customer info -->
                <div class="row mb-3" v-if="order.customer_name_display">
                  <div class="col-md-4">
                    <div class="mb-1 fw-semibold">Customer</div>
                    <router-link
                      v-if="order.customer"
                      :to="'/customers/' + order.customer"
                      class="text-decoration-none"
                    >
                      {{ order.customer_name_display }}
                    </router-link>
                  </div>
                </div>

                <!-- Device Specs (from catalog) -->
                <div class="row mb-3" v-if="order.device_model_details">
                  <div class="col-12">
                    <div class="mb-1 fw-semibold">Device Specs</div>
                    <div class="row">
                      <div class="col-md-3" v-if="order.device_model_details.cpu">
                        <small class="text-muted">CPU:</small> {{ order.device_model_details.cpu }}
                      </div>
                      <div class="col-md-3" v-if="order.device_model_details.ram">
                        <small class="text-muted">RAM:</small> {{ order.device_model_details.ram }}
                      </div>
                      <div class="col-md-3" v-if="order.device_model_details.storage">
                        <small class="text-muted">Storage:</small> {{ order.device_model_details.storage }}
                      </div>
                      <div class="col-md-3" v-if="order.device_model_details.screen_size">
                        <small class="text-muted">Screen:</small> {{ order.device_model_details.screen_size }}
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Per-Unit Listing Info (supervisor, when device model attached) -->
                <div class="row mb-3" v-if="isSupervisor && order.device_model_details">
                  <div class="col-12">
                    <div class="mb-1 fw-semibold">Listing Info</div>
                    <div v-if="!isLocked" class="row g-2">
                      <div class="col-md-3">
                        <label class="form-label small mb-0">Installed OS</label>
                        <select v-model="listingForm.installed_os" class="form-select form-select-sm">
                          <option value="">-- Select --</option>
                          <option value="Windows 11 Pro">Windows 11 Pro</option>
                          <option value="Windows 10 Pro">Windows 10 Pro</option>
                          <option value="macOS Sonoma">macOS Sonoma</option>
                          <option value="macOS Ventura">macOS Ventura</option>
                          <option value="ChromeOS">ChromeOS</option>
                          <option value="Linux">Linux</option>
                          <option value="Wiped (No OS)">Wiped (No OS)</option>
                        </select>
                      </div>
                      <div class="col-md-3">
                        <label class="form-label small mb-0">Cosmetic Grade</label>
                        <select v-model="listingForm.cosmetic_grade" class="form-select form-select-sm">
                          <option value="">-- Select --</option>
                          <option value="Excellent">Excellent</option>
                          <option value="Good">Good</option>
                          <option value="Fair">Fair</option>
                          <option value="Acceptable">Acceptable</option>
                        </select>
                      </div>
                      <div class="col-md-4">
                        <label class="form-label small mb-0">Cosmetic Notes</label>
                        <input v-model="listingForm.cosmetic_notes" type="text" class="form-control form-control-sm" placeholder="e.g. Minor scuff on lid" />
                      </div>
                      <div class="col-md-2 d-flex align-items-end">
                        <button class="btn btn-outline-primary btn-sm" @click="saveListingInfo" :disabled="savingListingInfo">
                          <span v-if="savingListingInfo" class="spinner-border spinner-border-sm me-1"></span>
                          Save
                        </button>
                      </div>
                    </div>
                    <div v-else class="row">
                      <div class="col-md-3" v-if="order.installed_os">
                        <small class="text-muted">OS:</small> {{ order.installed_os }}
                      </div>
                      <div class="col-md-3" v-if="order.cosmetic_grade">
                        <small class="text-muted">Grade:</small> {{ order.cosmetic_grade }}
                      </div>
                      <div class="col-md-6" v-if="order.cosmetic_notes">
                        <small class="text-muted">Notes:</small> {{ order.cosmetic_notes }}
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Shipping section -->
                <div class="row mb-3" v-if="order.tracking_number || order.shipping_carrier || order.shipping_status || isSupervisor">
                  <div class="col-12">
                    <div class="mb-1 fw-semibold">Shipping</div>
                    <div v-if="isSupervisor && !isLocked" class="row g-2">
                      <div class="col-md-3">
                        <label class="form-label small mb-0">Tracking #</label>
                        <input v-model="shippingForm.tracking_number" type="text" class="form-control form-control-sm" />
                      </div>
                      <div class="col-md-2">
                        <label class="form-label small mb-0">Carrier</label>
                        <select v-model="shippingForm.shipping_carrier" class="form-select form-select-sm">
                          <option value="">None</option>
                          <option value="UPS">UPS</option>
                          <option value="FEDEX">FedEx</option>
                          <option value="USPS">USPS</option>
                          <option value="DHL">DHL</option>
                          <option value="OTHER">Other</option>
                        </select>
                      </div>
                      <div class="col-md-2">
                        <label class="form-label small mb-0">Ship Date</label>
                        <input v-model="shippingForm.ship_date" type="date" class="form-control form-control-sm" />
                      </div>
                      <div class="col-md-2">
                        <label class="form-label small mb-0">Status</label>
                        <select v-model="shippingForm.shipping_status" class="form-select form-select-sm">
                          <option value="">None</option>
                          <option value="LABEL_CREATED">Label Created</option>
                          <option value="IN_TRANSIT">In Transit</option>
                          <option value="OUT_FOR_DELIVERY">Out for Delivery</option>
                          <option value="DELIVERED">Delivered</option>
                          <option value="EXCEPTION">Exception</option>
                        </select>
                      </div>
                      <div class="col-md-2 d-flex align-items-end">
                        <button class="btn btn-outline-primary btn-sm" @click="saveShipping" :disabled="savingShipping">
                          <span v-if="savingShipping" class="spinner-border spinner-border-sm me-1"></span>
                          Save
                        </button>
                      </div>
                    </div>
                    <div v-else class="row">
                      <div class="col-md-3" v-if="order.tracking_number">
                        <small class="text-muted">Tracking:</small>
                        <a v-if="trackingUrl" :href="trackingUrl" target="_blank" rel="noopener noreferrer">{{ order.tracking_number }}</a>
                        <span v-else>{{ order.tracking_number }}</span>
                      </div>
                      <div class="col-md-3" v-if="order.shipping_carrier">
                        <small class="text-muted">Carrier:</small> {{ order.shipping_carrier }}
                      </div>
                      <div class="col-md-3" v-if="order.ship_date">
                        <small class="text-muted">Ship Date:</small> {{ order.ship_date }}
                      </div>
                      <div class="col-md-3" v-if="order.shipping_status">
                        <small class="text-muted">Status:</small> {{ order.shipping_status }}
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Pricing fields (supervisor only) -->
                <div class="row mb-3" v-if="isSupervisor && (order.estimated_cost || order.actual_cost || order.sale_price || order.sold_price)">
                  <div class="col-md-3" v-if="order.estimated_cost">
                    <div class="mb-1 fw-semibold">Est. Cost</div>
                    <div class="text-muted">${{ order.estimated_cost }}</div>
                  </div>
                  <div class="col-md-3" v-if="order.actual_cost">
                    <div class="mb-1 fw-semibold">Actual Cost</div>
                    <div class="text-muted">${{ order.actual_cost }}</div>
                  </div>
                  <div class="col-md-3" v-if="order.sale_price">
                    <div class="mb-1 fw-semibold">Listing Price</div>
                    <div class="text-muted">${{ order.sale_price }}</div>
                  </div>
                  <div class="col-md-3" v-if="order.sold_price">
                    <div class="mb-1 fw-semibold">Sold Price</div>
                    <div class="text-muted">${{ order.sold_price }}</div>
                  </div>
                </div>

                <!-- eBay Listing Info (supervisor only) -->
                <div class="row mb-3" v-if="isSupervisor && order && (order.ebay_listing_url || order.ebay_item_id)">
                  <div class="col-12">
                    <div class="mb-1 fw-semibold">eBay Listing</div>
                    <div class="row">
                      <div class="col-md-4" v-if="order.ebay_item_id">
                        <small class="text-muted">Item ID:</small> {{ order.ebay_item_id }}
                      </div>
                      <div class="col-md-4" v-if="order.ebay_listing_url">
                        <small class="text-muted">Listing:</small>
                        <a :href="order.ebay_listing_url" target="_blank" rel="noopener noreferrer">View on eBay</a>
                      </div>
                      <div class="col-md-4" v-if="order.ebay_listing_status">
                        <small class="text-muted">Status:</small>
                        <span class="badge" :class="{
                          'bg-success': order.ebay_listing_status === 'ACTIVE',
                          'bg-dark': order.ebay_listing_status === 'SOLD',
                          'bg-secondary': order.ebay_listing_status === 'ENDED',
                        }">{{ order.ebay_listing_status }}</span>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Projected Revenue (supervisor only, when sale_price exists) -->
                <div class="row mb-3" v-if="isSupervisor && order && order.projected_ebay_fees">
                  <div class="col-12">
                    <div class="mb-1 fw-semibold">Projected Revenue</div>
                    <div class="row">
                      <div class="col-md-3">
                        <small class="text-muted">eBay Fees{{ order.ebay_fees ? ' (actual)' : ' (est.)' }}:</small>
                        ${{ order.ebay_fees || order.projected_ebay_fees }}
                      </div>
                      <div class="col-md-3">
                        <small class="text-muted">Net After Fees:</small>
                        ${{ order.projected_net_revenue }}
                      </div>
                      <div class="col-md-3" v-if="order.projected_margin !== null && order.projected_margin !== undefined">
                        <small class="text-muted">Margin:</small>
                        <span :class="{ 'text-danger': order.projected_margin < 0, 'text-success': order.projected_margin >= 0 }">
                          ${{ order.projected_margin }}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Sold info -->
                <div class="row mb-3" v-if="order.order_status === 'SOLD' && order.sold_at">
                  <div class="col-md-4">
                    <div class="mb-1 fw-semibold">Sold At</div>
                    <div class="text-muted">{{ new Date(order.sold_at).toLocaleString() }}</div>
                  </div>
                </div>

                <hr />

                <!-- Steps table header -->
                <div class="mb-2 d-flex justify-content-between align-items-center">
                  <h2 class="h6 mb-0">Steps</h2>
                </div>

                <!-- Steps table -->
                <div v-if="order.steps && order.steps.length" class="table-responsive">
                  <table class="table table-sm align-middle">
                    <thead class="table-light">
                      <tr>
                        <th style="width: 60px;">#</th>
                        <th>Step</th>
                        <th>Description</th>
                        <th style="width: 140px;">Status</th>
                        <th>Notes</th>
                        <th style="width: 80px;"></th>
                      </tr>
                    </thead>
                    <tbody>
                      <!-- Render each step row -->
                      <tr v-for="step in order.steps" :key="step.order_step_id">
                        <td>{{ step.step_position }}</td>
                        <td>{{ step.step_name }}</td>
                        <td class="text-muted small">
                          {{ step.step_description }}
                        </td>
                        <td>
                          <!-- Step status dropdown -->
                          <select
                            v-model="step.localStatus"
                            class="form-select form-select-sm"
                            @change="updateStep(step)"
                            :disabled="step.saving || isLocked"
                          >
                            <option value="PASSED">Passed</option>
                            <option value="FAILED">Failed</option>
                            <option value="SKIPPED">Skipped</option>
                          </select>
                          <!-- Spinner while saving -->
                          <span
                            v-if="step.saving"
                            class="spinner-border spinner-border-sm"
                          ></span>
                        </td>
                        <td>
                          <!-- Notes input, updates on blur -->
                          <input
                            v-model="step.localNotes"
                            type="text"
                            class="form-control form-control-sm"
                            placeholder="Notes"
                            @blur="updateStep(step)"
                            :disabled="step.saving || isLocked"
                          />
                        </td>
                        <td class="text-end">
                          <!-- (empty, reserved for future actions) -->
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                                </div>

               <!-- Issues section -->
<div class="mt-4">
  <div class="d-flex justify-content-between align-items-center mb-2">
    <h2 class="h6 mb-0">Issues</h2>
    <input
      v-model="issueSearch"
      type="text"
      class="form-control form-control-sm"
      style="max-width: 260px;"
      placeholder="Search issues (description, status, severity)..."
    />
  </div>

  <!-- No issues at all on this order -->
  <div
    v-if="!order || !Array.isArray(order.issues) || order.issues.length === 0"
    class="text-muted small"
  >
    No issues logged for this order.
  </div>

  <!-- We have issues; show filtered results -->
  <div v-else>
    <table
      v-if="filteredIssues.length > 0"
      class="table table-sm align-middle mb-1"
    >
      <thead class="table-light">
  <tr>
    <th style="width: 160px;">Created</th>
     <th style="width: 180px;">Step</th>
    <th style="width: 120px;">Severity</th>
    <th style="width: 140px;">Status</th>
    <th>Description</th>
    <th>Resolution</th>
    <th style="width: 160px;">Resolved By</th>
  </tr>
</thead>
      <tbody>
       <tr v-for="issue in filteredIssues" :key="issue.issue_id">
  <td class="small text-muted">
    {{ issue.created_timestamp }}
  </td>
   <td class="small">
      {{ issue.step_name || "—" }}           
    </td>
  <td class="small">
    {{ issue.issue_severity }}
  </td>
  <td class="small">
    <span v-if="!canEditIssues">
      {{ issue.issue_status }}
    </span>
    <select
      v-else
      v-model="issue.localStatus"
      class="form-select form-select-sm"
      :disabled="issue.saving"
      @change="updateIssue(issue)"
    >
      <option value="OPEN">OPEN</option>
      <option value="IN_PROGRESS">IN_PROGRESS</option>
      <option value="RESOLVED">RESOLVED</option>
      <option value="WONT_FIX">WONT_FIX</option>
    </select>
  </td>
  <td class="small">
    {{ issue.issue_description }}
  </td>
  <td class="small">
    <span v-if="issue.resolution_summary && issue.resolution_summary.trim()">
      {{ issue.resolution_summary }}
    </span>
    <span v-else>—</span>
  </td>
  <td class="small">
    <span v-if="issue.resolved_by_username">
      {{ issue.resolved_by_username }}
    </span>
    <span v-else>—</span>
  </td>
</tr>
      </tbody>
    </table>

    <div
      v-else
      class="text-muted small mt-1"
    >
      No issues match your search.
    </div>
  </div>
</div>

                <!-- Activity Timeline (collapsible) -->
                <div class="mt-4" v-if="order">
                  <div class="d-flex justify-content-between align-items-center mb-2">
                    <h2 class="h6 mb-0">Activity Timeline</h2>
                    <button class="btn btn-outline-secondary btn-sm" @click="toggleTimeline">
                      {{ showTimeline ? "Hide" : "Show" }}
                    </button>
                  </div>
                  <div v-if="showTimeline">
                    <div v-if="activityLoading" class="text-center py-2">
                      <div class="spinner-border spinner-border-sm" role="status"></div>
                    </div>
                    <ul v-else-if="activityLogs.length" class="list-group list-group-flush">
                      <li v-for="log in activityLogs" :key="log.id" class="list-group-item px-0 py-2 small">
                        <span class="fw-semibold">{{ log.username || "System" }}</span>
                        {{ log.action }}
                        <br />
                        <small class="text-muted">{{ new Date(log.created_at).toLocaleString() }}</small>
                      </li>
                    </ul>
                    <div v-else class="text-muted small">No activity recorded.</div>
                  </div>
                </div>

                <hr class="my-4" />

                <!-- Footer actions: navigation and status update -->
                <div class="d-flex justify-content-between">

                <div class="d-flex justify-content-between">
  <!-- Back + prev/next nav -->
  <div class="d-flex gap-2">
    <button
      class="btn btn-outline-secondary"
      @click="$router.push({ name: 'OrdersList' })"
    >
      Back to Devices
    </button>
    <button
      v-if="prevOrderId"
      class="btn btn-outline-secondary btn-sm"
      @click="goToOrder(prevOrderId)"
    >
      &larr; Previous
    </button>
    <button
      v-if="nextOrderId"
      class="btn btn-outline-secondary btn-sm"
      @click="goToOrder(nextOrderId)"
    >
      Next &rarr;
    </button>
  </div>

  <!-- Right-side actions -->
  <div class="d-flex gap-2">
    <!-- Technician: Mark for QA -->
    <button
      v-if="isTechnician && !isLocked"
      class="btn btn-warning"
      @click="markForQA"
      :disabled="updatingStatus"
    >
      <span
        v-if="updatingStatus"
        class="spinner-border spinner-border-sm me-1"
      ></span>
      Mark for QA
    </button>

    <!-- Supervisor: Reopen a completed order back to QA -->
    <button
      v-if="isSupervisor && isOrderComplete"
      class="btn btn-outline-warning btn-sm"
      @click="reopenOrder"
      :disabled="updatingStatus"
    >
      <span v-if="updatingStatus" class="spinner-border spinner-border-sm me-1"></span>
      Reopen Order
    </button>

    <!-- Supervisor/Admin: Mark Complete (while not complete yet) -->
    <button
      v-if="isSupervisor && order.order_status !== 'COMPLETE' && order.order_status !== 'SOLD'"
      class="btn btn-success"
      @click="completeOrder"
      :disabled="hasOpenIssues"
    >
      <span
        v-if="completing"
        class="spinner-border spinner-border-sm me-1"
      ></span>
      Mark Complete
    </button>

    <!-- Generate eBay Listing HTML -->
    <button
      v-if="isSupervisor && order && !isSold"
      class="btn btn-outline-success btn-sm"
      @click="ebayListingGeneratorModal.visible = true"
    >
      Generate Listing
    </button>

    <!-- Link eBay Listing (supervisor, order not sold yet) -->
    <button
      v-if="isSupervisor && !isSold && order && !order.ebay_listing_url"
      class="btn btn-outline-info btn-sm"
      @click="ebayListingModal.visible = true"
      :disabled="updatingStatus"
    >
      Link eBay Listing
    </button>

    <!-- Import from eBay (only when COMPLETE + supervisor) -->
    <button
      v-if="isOrderComplete && isSupervisor"
      class="btn btn-outline-dark"
      @click="ebayImportModal.visible = true"
      :disabled="updatingStatus"
    >
      Import from eBay
    </button>

    <!-- Mark Sold (only when status is COMPLETE) -->
    <button
      v-if="isOrderComplete"
      class="btn btn-dark"
      @click="showSoldModal"
      :disabled="updatingStatus"
    >
      <span
        v-if="updatingStatus"
        class="spinner-border spinner-border-sm me-1"
      ></span>
      Mark Sold
    </button>

    <span
      v-if="isSold"
      class="badge bg-dark align-self-center"
    >
      Sold
    </span>
  </div>
</div>
</div>
            </div>
          </div>
        </div>
      </div>
    </main>

    <!-- Issue Description Modal (phase 1 of issue creation) -->
    <InputModal
      :visible="issueModal.visible && issueModal.phase === 'description'"
      title="Create Issue for Failed Step"
      message="Describe the issue found during this step."
      input-type="textarea"
      input-label="Issue Description"
      placeholder="Describe what went wrong..."
      :required="true"
      confirm-text="Next"
      @confirm="onIssueDescriptionConfirm"
      @cancel="onIssueModalCancel"
    />

    <!-- Issue Severity Modal (phase 2 of issue creation) -->
    <InputModal
      :visible="issueModal.visible && issueModal.phase === 'severity'"
      title="Issue Severity"
      message="Select the severity level for this issue."
      input-type="select"
      input-label="Severity"
      :options="severityOptions"
      :initial-value="issueModal.severity"
      confirm-text="Create Issue"
      @confirm="onIssueSeverityConfirm"
      @cancel="onIssueModalCancel"
    />

    <!-- Resolution Summary Modal -->
    <InputModal
      :visible="resolutionModal.visible"
      title="Resolution Summary"
      message="Enter a summary explaining how this issue was resolved."
      input-type="textarea"
      input-label="Resolution Summary"
      placeholder="Describe how the issue was resolved..."
      :initial-value="resolutionModal.summary"
      :required="true"
      confirm-text="Save"
      @confirm="onResolutionConfirm"
      @cancel="onResolutionCancel"
    />

    <!-- Sold Price Modal -->
    <InputModal
      :visible="soldModal.visible"
      title="Mark as Sold"
      message="Enter the sold price for this device."
      input-type="text"
      input-label="Sold Price ($)"
      placeholder="0.00"
      :required="false"
      confirm-text="Mark Sold"
      @confirm="onSoldConfirm"
      @cancel="onSoldCancel"
    />

    <!-- eBay Import Modal -->
    <InputModal
      :visible="ebayImportModal.visible"
      title="Import from eBay"
      message="Enter the eBay order ID to import sale data."
      input-type="text"
      input-label="eBay Order ID"
      placeholder="12-34567-89012"
      :required="true"
      confirm-text="Import"
      @confirm="onEbayImportConfirm"
      @cancel="ebayImportModal.visible = false"
    />

    <!-- eBay Listing Link Modal -->
    <InputModal
      :visible="ebayListingModal.visible"
      title="Link eBay Listing"
      message="Enter the eBay item ID to link this order to its listing. This will pull the listing price and URL."
      input-type="text"
      input-label="eBay Item ID"
      placeholder="123456789012"
      :required="true"
      confirm-text="Link Listing"
      @confirm="onEbayListingLinkConfirm"
      @cancel="ebayListingModal.visible = false"
    />

    <!-- eBay Listing Generator Modal -->
    <EbayListingModal
      :visible="ebayListingGeneratorModal.visible"
      :order="order"
      @close="ebayListingGeneratorModal.visible = false"
    />
  </div>
</template>

<script>
import { APIService } from "../http/APIService";
import InputModal from "./InputModal.vue";
import EbayListingModal from "./EbayListingModal.vue";

export default {
  name: "OrderDetail",
  components: {
    InputModal,
    EbayListingModal,
  },
  data() {
    return {
      order: null,             // Loaded order object
      loading: false,          // Loading state for order fetch
      completing: false,       // Loading state for completing order
      updatingStatus: false,   // Loading state for status update
      updatingTemplate: false, // Loading state for template change
      localOrderStatus: null,  // Local copy of order status for dropdown
      error: null,             // Error message
      api: new APIService(),   // API service instance
      currentUser: null,       // Current user object
      templates: [],           // Available refurbish templates
      selectedTemplateId: null, // Currently selected template id in dropdown
      issueSearch: "",

      // Issue creation modal state (when step fails)
      issueModal: {
        visible: false,
        step: null,              // Step that triggered the modal
        phase: "description",    // 'description' or 'severity'
        description: "",
        severity: "MEDIUM",
      },

      // Resolution summary modal state (when closing an issue)
      resolutionModal: {
        visible: false,
        issue: null,             // Issue being resolved
        newStatus: null,         // Target status (RESOLVED or WONT_FIX)
        previousStatus: null,    // Previous status to revert to if cancelled
        summary: "",
      },

      // Sold price modal state
      soldModal: {
        visible: false,
      },

      // eBay import modal state
      ebayImportModal: {
        visible: false,
      },

      // eBay listing link modal state
      ebayListingModal: {
        visible: false,
      },

      // eBay listing generator modal state
      ebayListingGeneratorModal: {
        visible: false,
      },

      // Shipping form
      shippingForm: {
        tracking_number: "",
        shipping_carrier: "",
        ship_date: "",
        shipping_status: "",
      },
      savingShipping: false,

      // Per-unit listing fields
      listingForm: {
        installed_os: "",
        cosmetic_grade: "",
        cosmetic_notes: "",
      },
      savingListingInfo: false,

      // Device model selector
      deviceModels: [],
      selectedDeviceModelId: null,
      savingDeviceModel: false,
      editingDeviceModel: false,

      // Activity timeline
      showTimeline: false,
      activityLogs: [],
      activityLoading: false,

      // Navigation between orders
      navList: [],
    };
  },
  created() {
    this.loadCurrentUser();
    this.fetchOrder();
    this.fetchTemplates();
    this.fetchDeviceModels();
    this.loadNavList();
  },
  watch: {
    "$route.params.id"() {
      this.fetchOrder();
      this.showTimeline = false;
      this.activityLogs = [];
    },
  },
  computed: {

    hasOpenIssues() {
  if (!this.order || !Array.isArray(this.order.issues)) return false;

  return this.order.issues.some((issue) => {
    return issue.issue_status !== "RESOLVED" &&
           issue.issue_status !== "WONT_FIX";
  });
},

    filteredIssues() {
    if (!this.order || !Array.isArray(this.order.issues)) {
      return [];
    }

    const q = (this.issueSearch || "").trim().toLowerCase();
    if (!q) {
      return this.order.issues;
    }

    return this.order.issues.filter((issue) => {
      const desc = (issue.issue_description || "").toLowerCase();
      const status = (issue.issue_status || "").toLowerCase();
      const severity = (issue.issue_severity || "").toLowerCase();

      return (
        desc.includes(q) ||
        status.includes(q) ||
        severity.includes(q)
      );
    });
  },

    canEditIssues() {
      return this.isSupervisor || this.isTechnician;
    },

    // Lowercase user role for checks
    userRole() {
      return (this.currentUser?.role || "").toLowerCase();
    },
    // Is the user a technician?
    isTechnician() {
      return this.userRole === "technician";
    },
    // Is the user a supervisor or admin?
    isSupervisor() {
      return this.userRole === "supervisor" || this.userRole === "admin";
    },
    // Allowed transitions from API response
    allowedTransitions() {
      return this.order?.allowed_transitions || [];
    },
    isOrderComplete() {
      return this.order?.order_status === "COMPLETE";
    },
    isSold() {
      return this.order?.order_status === "SOLD";
    },
    isLocked() {
      if (!this.order) return false;
      return ["COMPLETE", "SOLD", "CANCELLED"].includes(this.order.order_status);
    },
    statusBadgeClass() {
      const s = this.order?.order_status;
      if (s === "COMPLETE") return "bg-success";
      if (s === "SOLD") return "bg-dark";
      if (s === "CANCELLED") return "bg-secondary";
      if (s === "HOLD") return "bg-warning text-dark";
      return "bg-primary";
    },
    prevOrderId() {
      const currentId = Number(this.$route.params.id);
      const idx = this.navList.indexOf(currentId);
      return idx > 0 ? this.navList[idx - 1] : null;
    },
    nextOrderId() {
      const currentId = Number(this.$route.params.id);
      const idx = this.navList.indexOf(currentId);
      return idx >= 0 && idx < this.navList.length - 1 ? this.navList[idx + 1] : null;
    },
    canPrintOrder() {
      if (!this.order) return false;

      // Supervisors/admins: always
      if (this.isSupervisor) return true;

      // Technicians: only when complete
      if (this.isTechnician && this.isOrderComplete) return true;

      // Everyone else: no
      return false;
    },
    currentTemplateDisplay() {
      if (!this.order) return "";
      return this.order.template_name || `Template #${this.order.template}`;
    },
    trackingUrl() {
      if (!this.order || !this.order.tracking_number) return null;
      const num = this.order.tracking_number;
      const carrier = (this.order.shipping_carrier || "").toUpperCase();
      const urls = {
        UPS: `https://www.ups.com/track?tracknum=${encodeURIComponent(num)}`,
        FEDEX: `https://www.fedex.com/fedextrack/?trknbr=${encodeURIComponent(num)}`,
        USPS: `https://tools.usps.com/go/TrackConfirmAction?tLabels=${encodeURIComponent(num)}`,
        DHL: `https://www.dhl.com/us-en/home/tracking/tracking-global-forwarding.html?submit=1&tracking-id=${encodeURIComponent(num)}`,
      };
      return urls[carrier] || null;
    },
    // Options for issue severity dropdown
    severityOptions() {
      return [
        { value: "LOW", label: "Low" },
        { value: "MEDIUM", label: "Medium" },
        { value: "HIGH", label: "High" },
        { value: "CRITICAL", label: "Critical" },
      ];
    },
  },
  methods: {
    canModifyIssue(issue) {
  return issue.issue_status !== "RESOLVED" &&
         issue.issue_status !== "WONT_FIX" &&
         !this.isSold;
},


    loadNavList() {
      try {
        const raw = sessionStorage.getItem("orderNavList");
        this.navList = raw ? JSON.parse(raw) : [];
      } catch {
        this.navList = [];
      }
    },

    goToOrder(id) {
      this.$router.push({ name: "OrderDetail", params: { id } });
    },

    // Load current user from localStorage
    loadCurrentUser() {
      const raw = localStorage.getItem("currentUser");
      try {
        this.currentUser = raw ? JSON.parse(raw) : null;
      } catch {
        this.currentUser = null;
      }
    },

    updatePageTitle() {
  if (!this.order) return;
  const common = this.order.device_commonname || "";
  const serial = this.order.serial_number || "";
  document.title = `${common} ${serial} | Second Wind Refurbish`;
},


    // Fetch order details from API
    async fetchOrder() {
      this.loading = true;
      this.error = null;
      try {
        const id = this.$route.params.id;
        const accessToken = localStorage.getItem("accessToken");
        const res = await this.api.getOrder(id, accessToken);
        const order = res.data;

        // Prepare steps with local state for UI
        if (Array.isArray(order.steps)) {
          order.steps = order.steps.map((s) => {
            const initialStatus = s.step_status || "PASSED";
            return {
              ...s,
              localStatus: initialStatus,
              localNotes: s.notes || "",
              previousStatus: initialStatus, // used to detect transitions into FAILED
              saving: false,
            };
          });
        } else {
          order.steps = [];
        }
        if (Array.isArray(order.issues)) {
          order.issues = order.issues.map((issue) => ({
            ...issue,
            localStatus: issue.issue_status,
        
            saving: false,
          }));
        } else {
          order.issues = [];
        }

        this.order = order;
        this.localOrderStatus = order.order_status;
        this.selectedTemplateId = order.template; // keep dropdown in sync
        this.shippingForm = {
          tracking_number: order.tracking_number || "",
          shipping_carrier: order.shipping_carrier || "",
          ship_date: order.ship_date || "",
          shipping_status: order.shipping_status || "",
        };
        this.listingForm = {
          installed_os: order.installed_os || "",
          cosmetic_grade: order.cosmetic_grade || "",
          cosmetic_notes: order.cosmetic_notes || "",
        };
        this.selectedDeviceModelId = order.device_model_ref || null;
        this.editingDeviceModel = false;

this.updatePageTitle();

      } catch (err) {
        this.error = "Unable to load order.";
      } finally {
        this.loading = false;
      }
    },

    // Fetch template list (for supervisors)
    async fetchTemplates() {
      if (!this.isSupervisor) return;
      try {
        const accessToken = localStorage.getItem("accessToken");
        const res = await this.api.getTemplates(accessToken);
        this.templates = Array.isArray(res.data) ? res.data : [];
      } catch {
        // silent fail; dropdown will show "Loading templates..." / do nothing
      }
    },

    // Update a single step's status/notes via API
     // Update a single step's status/notes via API
    async updateStep(step) {
      if (!this.order) return;

      const previousStatus = step.previousStatus || step.step_status || "PASSED";
      const newStatus = step.localStatus;

      // Only techs get prompted, and only on transition into FAILED
      const shouldPromptForIssue =
        (this.isTechnician || this.isSupervisor) &&
        previousStatus !== "FAILED" &&
        newStatus === "FAILED";

      const accessToken = localStorage.getItem("accessToken");

      try {
        step.saving = true;

        await this.api.updateOrderStep(
          this.order.order_id,
          step.order_step_id,
          {
            step_status: step.localStatus,
            notes: step.localNotes,
          },
          accessToken
        );

        // Sync local + previous values to what we just saved
        step.step_status = step.localStatus;
        step.notes = step.localNotes;
        step.previousStatus = step.localStatus;

        if (shouldPromptForIssue) {
          // Open the issue creation modal
          this.issueModal.step = step;
          this.issueModal.description = "";
          this.issueModal.severity = "MEDIUM";
          this.issueModal.phase = "description";
          this.issueModal.visible = true;
        }
      } catch (err) {
        console.error("Failed to update step:", err);
        this.error = "Unable to update step.";
        // Revert local status on failure so we don't get stuck in a bad state
        step.localStatus = previousStatus;
      } finally {
        step.saving = false;
      }
    },

    // Issue modal: description confirmed, move to severity selection
    onIssueDescriptionConfirm(description) {
      this.issueModal.description = description;
      this.issueModal.phase = "severity";
    },

    // Issue modal: severity confirmed, create the issue
    async onIssueSeverityConfirm(severity) {
      const step = this.issueModal.step;
      if (!step || !this.order) {
        this.onIssueModalCancel();
        return;
      }

      try {
        const accessToken = localStorage.getItem("accessToken");
        const payload = {
          issue_description: this.issueModal.description.trim(),
          issue_severity: severity,
          issue_status: "OPEN",
          step: step.step_id,
        };

        await this.api.createOrderIssue(
          this.order.order_id,
          payload,
          accessToken
        );

        // Close modal and reload order
        this.issueModal.visible = false;
        await this.fetchOrder();
      } catch (err) {
        console.error("Failed to create issue:", err);
        this.error = "Unable to create issue for this step.";
        this.issueModal.visible = false;
      }
    },

    // Issue modal: cancelled
    onIssueModalCancel() {
      this.issueModal.visible = false;
      this.issueModal.step = null;
      this.issueModal.description = "";
      this.issueModal.severity = "MEDIUM";
      this.issueModal.phase = "description";
    },

    async updateIssue(issue) {
      if (!this.order) return;

      const previousStatus = issue.issue_status;
      const newStatus = issue.localStatus;

      // If moving to RESOLVED or WONT_FIX, show resolution modal
      if (newStatus === "RESOLVED" || newStatus === "WONT_FIX") {
        const existing = issue.localResolutionSummary || issue.resolution_summary || "";
        this.resolutionModal.issue = issue;
        this.resolutionModal.newStatus = newStatus;
        this.resolutionModal.previousStatus = previousStatus;
        this.resolutionModal.summary = existing;
        this.resolutionModal.visible = true;
        return;
      }

      // For other status changes, update directly
      await this.saveIssueUpdate(issue, newStatus, previousStatus, null);
    },

    // Resolution modal: confirmed with summary
    async onResolutionConfirm(summary) {
      const { issue, newStatus, previousStatus } = this.resolutionModal;
      if (!issue) {
        this.onResolutionCancel();
        return;
      }

      issue.localResolutionSummary = summary.trim();
      this.resolutionModal.visible = false;

      await this.saveIssueUpdate(issue, newStatus, previousStatus, summary.trim());
    },

    // Resolution modal: cancelled
    onResolutionCancel() {
      const { issue, previousStatus } = this.resolutionModal;
      if (issue) {
        issue.localStatus = previousStatus;
      }
      this.resolutionModal.visible = false;
      this.resolutionModal.issue = null;
      this.resolutionModal.newStatus = null;
      this.resolutionModal.previousStatus = null;
      this.resolutionModal.summary = "";
    },

    // Helper to save issue status update
    async saveIssueUpdate(issue, newStatus, previousStatus, resolutionSummary) {
      const accessToken = localStorage.getItem("accessToken");
      const payload = { issue_status: newStatus };

      if (resolutionSummary) {
        payload.resolution_summary = resolutionSummary;
      }

      try {
        issue.saving = true;

        await this.api.updateOrderIssue(
          this.order.order_id,
          issue.issue_id,
          payload,
          accessToken
        );

        // Sync local status
        issue.issue_status = newStatus;

        // Refetch so resolved_by_username and timestamps are fresh
        await this.fetchOrder();
      } catch (err) {
        console.error("Failed to update issue:", err);
        this.error = "Unable to update issue.";
        // revert local selection on failure
        issue.localStatus = previousStatus;
      } finally {
        issue.saving = false;
      }
    },

    // Update the order's status via API
    async updateOrderStatus(newStatus) {
      if (!this.order) return;
      this.updatingStatus = true;
      this.error = null;

      const previous = this.order.order_status;

      try {
        const accessToken = localStorage.getItem("accessToken");
        await this.api.updateOrderStatus(
          this.order.order_id,
          { order_status: newStatus },
          accessToken
        );
        await this.fetchOrder();
      } catch (e) {
        const detail = e.response?.data?.order_status || e.response?.data?.detail;
        this.error = detail ? `Status change failed: ${detail}` : "Unable to update order status.";
        this.localOrderStatus = previous;
      } finally {
        this.updatingStatus = false;
      }
    },

    // Handler for status dropdown change
    onStatusChange() {
      const targetStatus = this.localOrderStatus;
      this.updateOrderStatus(targetStatus);
    },

    // Supervisor-only: change template
    async onTemplateChange() {
      const newId = this.selectedTemplateId;
      if (!this.order || !newId) return;
      if (newId === this.order.template) return; // no-op

      this.updatingTemplate = true;
      this.error = null;

      try {
        const accessToken = localStorage.getItem("accessToken");
        // Re-use updateOrderStatus API method – it just does PATCH /orders/<id>/
        await this.api.updateOrderStatus(
          this.order.order_id,
          { template: newId },
          accessToken
        );
        // Reload order so steps are rebuilt server-side
        await this.fetchOrder();
      } catch (e) {
        this.error = "Unable to update template.";
        this.selectedTemplateId = this.order.template;
      } finally {
        this.updatingTemplate = false;
      }
    },

    // Mark order for QA (for technicians)
    async markForQA() {
      this.localOrderStatus = "QA";
      await this.updateOrderStatus("QA");
    },

    // Supervisor: Reopen a completed order back to QA
    async reopenOrder() {
      if (!confirm("Reopen this order? It will be set back to QA so you can update steps.")) return;
      await this.updateOrderStatus("QA");
    },

    showSoldModal() {
      this.soldModal.visible = true;
    },

    async onSoldConfirm(priceStr) {
      this.soldModal.visible = false;
      const price = priceStr ? parseFloat(priceStr) : null;
      this.updatingStatus = true;
      this.error = null;
      try {
        const accessToken = localStorage.getItem("accessToken");
        const payload = { order_status: "SOLD" };
        if (price && !isNaN(price)) {
          payload.sold_price = price.toFixed(2);
        }
        await this.api.updateOrderStatus(this.order.order_id, payload, accessToken);
        await this.fetchOrder();
      } catch (e) {
        const detail = e.response?.data?.order_status || e.response?.data?.detail;
        this.error = detail ? `Unable to mark sold: ${detail}` : "Unable to mark sold.";
      } finally {
        this.updatingStatus = false;
      }
    },

    onSoldCancel() {
      this.soldModal.visible = false;
    },

    async onEbayImportConfirm(ebayOrderId) {
      this.ebayImportModal.visible = false;
      if (!ebayOrderId || !this.order) return;

      this.updatingStatus = true;
      this.error = null;
      try {
        const accessToken = localStorage.getItem("accessToken");
        const resp = await this.api.importEbayOrder(
          { order_id: this.order.order_id, ebay_order_id: ebayOrderId.trim() },
          accessToken
        );
        const data = resp.data;
        const parts = [];
        if (data.buyer_name) parts.push(`Buyer: ${data.buyer_name}`);
        if (data.sold_price) parts.push(`Price: $${data.sold_price}`);
        alert(`eBay import successful. ${parts.join(", ")}`);
        await this.fetchOrder();
      } catch (e) {
        const detail = e.response?.data?.detail;
        this.error = detail ? `eBay import failed: ${detail}` : "Unable to import eBay order.";
      } finally {
        this.updatingStatus = false;
      }
    },

    async onEbayListingLinkConfirm(ebayItemId) {
      this.ebayListingModal.visible = false;
      if (!ebayItemId || !this.order) return;

      this.updatingStatus = true;
      this.error = null;
      try {
        const accessToken = localStorage.getItem("accessToken");
        const resp = await this.api.linkEbayListing(
          this.order.order_id,
          ebayItemId.trim(),
          accessToken
        );
        const data = resp.data;
        const parts = [];
        if (data.listing_price) parts.push(`Listing price: $${data.listing_price}`);
        if (data.listing_status) parts.push(`Status: ${data.listing_status}`);
        alert(`eBay listing linked. ${parts.join(", ")}`);
        await this.fetchOrder();
      } catch (e) {
        const detail = e.response?.data?.detail;
        this.error = detail ? `eBay listing link failed: ${detail}` : "Unable to link eBay listing.";
      } finally {
        this.updatingStatus = false;
      }
    },

    // Mark order as complete (for supervisors/admins)
    async completeOrder() {
      // Warn if any steps are not PASSED
      const nonPassed = (this.order.steps || []).filter(
        (s) => s.localStatus !== "PASSED"
      );
      if (nonPassed.length > 0) {
        const names = nonPassed.map((s) => `${s.step_name} (${s.localStatus})`).join(", ");
        if (!confirm(`${nonPassed.length} step(s) not passed: ${names}.\n\nComplete this order anyway?`)) {
          return;
        }
      }

      this.completing = true;
      this.error = null;
      try {
        const accessToken = localStorage.getItem("accessToken");
        await this.api.completeOrder(this.order.order_id, accessToken);
        await this.fetchOrder();
      } catch (e) {
        const detail = e.response?.data?.order_status || e.response?.data?.detail;
        this.error = detail ? `Cannot complete: ${detail}` : "Unable to complete order.";
      } finally {
        this.completing = false;
      }
    },

    // Open the print view for the order
    openPrintView() {
      const orderId = this.order?.order_id;
      if (orderId) {
        this.$router.push({ name: "PrintOrder", params: { id: orderId } });
      }
    },

    async toggleTimeline() {
      this.showTimeline = !this.showTimeline;
      if (this.showTimeline && this.activityLogs.length === 0) {
        await this.fetchActivity();
      }
    },

    async fetchActivity() {
      if (!this.order) return;
      this.activityLoading = true;
      try {
        const accessToken = localStorage.getItem("accessToken");
        const res = await this.api.getOrderActivity(this.order.order_id, accessToken);
        this.activityLogs = res.data || [];
      } catch {
        // silent fail
      } finally {
        this.activityLoading = false;
      }
    },

    async fetchDeviceModels() {
      if (!this.isSupervisor) return;
      try {
        const accessToken = localStorage.getItem("accessToken");
        const res = await this.api.getDeviceModels(accessToken);
        this.deviceModels = Array.isArray(res.data) ? res.data : [];
      } catch {
        // silent fail
      }
    },

    async saveListingInfo() {
      if (!this.order) return;
      this.savingListingInfo = true;
      this.error = null;
      try {
        const accessToken = localStorage.getItem("accessToken");
        await this.api.updateOrderStatus(this.order.order_id, this.listingForm, accessToken);
        await this.fetchOrder();
      } catch {
        this.error = "Unable to save listing info.";
      } finally {
        this.savingListingInfo = false;
      }
    },

    async onDeviceModelChange() {
      if (!this.order) return;
      const newId = this.selectedDeviceModelId;
      this.savingDeviceModel = true;
      this.error = null;
      try {
        const accessToken = localStorage.getItem("accessToken");
        await this.api.updateOrderStatus(
          this.order.order_id,
          { device_model_ref: newId || null },
          accessToken
        );
        await this.fetchOrder();
      } catch {
        this.error = "Unable to update device model.";
        this.selectedDeviceModelId = this.order.device_model_ref || null;
      } finally {
        this.savingDeviceModel = false;
      }
    },

    async saveShipping() {
      if (!this.order) return;
      this.savingShipping = true;
      this.error = null;
      try {
        const accessToken = localStorage.getItem("accessToken");
        await this.api.updateOrderStatus(this.order.order_id, this.shippingForm, accessToken);
        await this.fetchOrder();
      } catch {
        this.error = "Unable to save shipping info.";
      } finally {
        this.savingShipping = false;
      }
    },

    printSerialLabel() {
      if (!this.order) return;

      const serial = (this.order.serial_number || "").trim();
      if (!serial) {
        alert("No serial number found on this order.");
        return;
      }

      const printWindow = window.open(
        "",
        "",
        "width=300,height=200"
      );

      printWindow.serial = serial;

      printWindow.document.write(`
        <html>
          <head>
            <title>Serial Label</title>
            <style>
              @page {
                size: 2in 1in;
                margin: 0;
              }
              html, body {
                width: 2in;
                height: 1in;
                margin: 0;
                padding: 0;
                overflow: hidden;
              }
              body {
                display: flex;
                flex-direction: column;
                align-items: center;
                justify-content: center;
                font-family: Arial, sans-serif;
              }
              .label-wrapper {
                width: 2in;
                height: 1in;
                display: flex;
                flex-direction: column;
                justify-content: center;
                align-items: center;
                overflow: hidden;
              }
              svg#barcode {
                width: 1.8in;
                height: 0.6in;
              }
              .serial-text {
                font-size: 0.14in;
                line-height: 1;
                margin-top: 0.03in;
                text-align: center;
                white-space: nowrap;
              }
            </style>
          </head>
          <body>
            <div class="label-wrapper">
              <svg id="barcode"></svg>
              <div class="serial-text"></div>
            </div>

            <script src="https://cdn.jsdelivr.net/npm/jsbarcode@3.11.6/dist/JsBarcode.all.min.js"><\/script>
            <script>
              window.addEventListener('load', function () {
                var serial = window.serial;
                document.querySelector('.serial-text').textContent = serial;
                JsBarcode("#barcode", serial, {
                  format: "CODE128",
                  displayValue: false,
                  margin: 0,
                  width: 1.2,
                  height: 40
                });
                setTimeout(() => window.print(), 200);
              });
            <\/script>
          </body>
        </html>
      `);

      printWindow.document.close();
    },
  }
};
</script>
